/**
 * Routing hooks for React Router v6 integration
 * Provides hooks for route information, navigation, URL building, and active route checking
 */
import { RouteMapContext } from "./RouteMapContext";
import { buildPathWithParams, matchPathToRouteName } from "./routeHelpers";
import { useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export interface RouteInfo {
  name: string;
  path: string;
  params: Record<string, any>;
  isValidRoute: boolean;
}

/**
 * Hook that returns current route information
 * Replaces reading from state.router.route in Redux
 *
 * @example
 * const route = useRouteInfo();
 * // { name: "pages.events", path: "/site/123/pages/events", params: {} }
 */
export function useRouteInfo(): RouteInfo {
  const location = useLocation();
  const params = useParams();
  const { pathMap } = useContext(RouteMapContext);

  return useMemo(() => {
    // Match current path against route map to get route name
    const matched = matchPathToRouteName(location.pathname, pathMap);

    if (matched) {
      return {
        isValidRoute: true,
        name: matched.name,
        params: { ...matched.params, ...params },
        path: location.pathname,
      };
    }

    // Fallback: extract last segment as route name
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const routeName =
      pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : "home";

    return {
      isValidRoute: false,
      name: routeName,
      params: params || {},
      path: location.pathname,
    };
  }, [location.pathname, params, pathMap]);
}

export type NavigateToFunction = (
  routeName: string,
  params?: Record<string, any>,
  options?: { replace?: boolean; state?: any },
) => void;

/**
 * Hook for programmatic navigation using named routes
 * Replaces Redux-based actions.navigateTo pattern
 *
 * @example
 * const navigateTo = useRouterNavigation();
 * navigateTo("pages.events", { id: 123 });
 * navigateTo("pages");
 */
export const useRouterNavigation = (): NavigateToFunction => {
  const navigate = useNavigate();
  const { defaultParamsMap, pathMap } = useContext(RouteMapContext);

  return useCallback(
    (routeName: string, params = {}, options = {}) => {
      // Build the path from route name and params (includes defaultParams)
      const path = buildPathWithParams(
        routeName,
        params,
        pathMap,
        defaultParamsMap,
      );

      // Navigate using React Router
      navigate(path, options);
    },
    [defaultParamsMap, navigate, pathMap],
  );
};

export type BuildUrlFunction = (
  routeName: string,
  params?: Record<string, any>,
) => string;

/**
 * Hook for building URLs from route names and parameters
 *
 * @example
 * const buildUrl = useBuildUrl();
 * const url = buildUrl("pages.custom", { id: 123, slug: "my-page" });
 * // Returns: "/pages/123/my-page?siteType=onlineInvitation"
 */
export const useBuildUrl = (): BuildUrlFunction => {
  const { defaultParamsMap, pathMap } = useContext(RouteMapContext);

  return useCallback(
    (routeName: string, params: Record<string, any> = {}): string => {
      return buildPathWithParams(routeName, params, pathMap, defaultParamsMap);
    },
    [defaultParamsMap, pathMap],
  );
};

export type RouteActiveFunction = (
  routeName: string,
  params?: Record<string, any>,
  exact?: boolean,
) => boolean;

/**
 * Hook for checking if a route is currently active
 *
 * @example
 * const isActive = useRouteActive();
 * if (isActive("pages.events", { id: 123 })) {
 *   // Current route is pages.events with id=123
 * }
 * if (isActive("events", {}, true)) {
 *   // Exact match for "events" route
 * }
 */
export const useRouteActive = (): RouteActiveFunction => {
  const location = useLocation();
  const { pathMap } = useContext(RouteMapContext);

  return useCallback(
    (
      routeName: string,
      params: Record<string, any> = {},
      exact = false,
    ): boolean => {
      const targetPath = buildPathWithParams(routeName, params, pathMap);
      const currentPath = location.pathname;

      if (exact) {
        return currentPath === targetPath;
      }

      return currentPath.startsWith(targetPath);
    },
    [location.pathname, pathMap],
  );
};
